'use client';

import { useState } from 'react';
import { ChevronRight, CalendarDays } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { dateParts, fmtTime } from '@/lib/format';
import type { Sibling } from '@/lib/types';

/**
 * THE OTHER DATES, WITHOUT THE PAGE BECOMING A CALENDAR
 * ====================================================
 *
 * A recurring event in MinistryPlatform is not one row with a rule on it. It
 * is one Event per occurrence, tied together by `dp_Sequence_Records`, and a
 * Bible study that has been running for years is a long list of them. This
 * section used to print every single one: Faithful Friends rendered 274 rows,
 * sixteen thousand pixels of them, which was eighty-nine per cent of the page.
 * What somebody wants from a page about Sunday morning is Sunday morning, and
 * then the reassurance that it happens again.
 *
 * So the page shows the next few and says how many there are. The rest are a
 * tap away, in a sheet, because a hundred dates is a thing to look through
 * rather than a thing to read past on the way to the agenda.
 *
 * FIVE, because it is enough to show the shape of the thing — weekly, monthly,
 * the second Tuesday — without the eye giving up. A series with six dates
 * shows five and offers one more, which looks silly, so the whole list stays
 * on the page until there are enough of them to be worth hiding.
 *
 * The count on the button is the WHOLE series, not the remainder. "View all 66
 * dates" tells somebody how long this thing runs, which is the question they
 * were about to ask; "59 more" makes them do arithmetic to find out.
 *
 * AND WHEN ONE SIGN-UP TAKES ALL OF THEM, IT SAYS SO HERE, ABOVE THE DATES.
 * `Events.Register_Into_Series` means MP's nightly service walks the sequence
 * and copies a registration forward onto every future date. One invoice covers
 * the lot and cancelling propagates the same way. Somebody who signs up on the
 * Monday is in eight events by Tuesday morning, and being told that afterwards
 * is not being told. It is deliberately the first thing in this section rather
 * than a footnote under the list: it changes what the button at the top of the
 * page means, so it has to be read before anybody reaches it.
 */

/**
 * ONE SIGN-UP, EVERY DATE — said once, under the button it is about.
 *
 * This is a footnote to the act of registering, not a thing to announce. It
 * used to be a bordered teal callout carrying an icon, and it appeared both
 * above the button and again over the date list, which made a small piece of
 * good news read as a warning printed twice. It is now quiet type under the
 * button, where somebody has just decided to press it and the only remaining
 * question is whether they have to do this again next month.
 *
 * It stays in the sheet's last step as well: the sheet is a different route
 * to the same button and a person takes exactly one of them.
 */
export function SeriesNote({ dates, className = '' }: { dates: number; className?: string }) {
  return (
    <p className={`text-slate text-center text-xs ${className}`}>
      One sign-up covers all {dates} dates.
    </p>
  );
}

const MAX_ON_PAGE = 5;
/** Below this, hiding some is more fuss than the rows are worth. */
const WORTH_HIDING = 8;

function DateRow({ s, detailsUrl }: { s: Sibling; detailsUrl: string }) {
  const p = dateParts(s.start);
  return (
    <li>
      <a
        href={`${detailsUrl}${s.id}`}
        className="hover:bg-mist flex items-center gap-3 px-4 py-3 transition"
      >
        <span className="bg-mist flex w-11 shrink-0 flex-col items-center rounded-xl py-1 leading-none">
          <span className="text-slate text-[0.55rem] font-bold tracking-widest uppercase">
            {p.month}
          </span>
          <span className="text-navy text-base font-extrabold">{p.day}</span>
        </span>
        <span className="min-w-0 flex-1 text-sm">
          <span className="text-navy block font-semibold">
            {p.weekday} · {fmtTime(s.start)}
          </span>
          <span className="text-slate block text-xs">
            {[s.campusName, s.locationName].filter(Boolean).join(' · ')}
          </span>
        </span>
        <ChevronRight className="text-line h-4 w-4 shrink-0" />
      </a>
    </li>
  );
}

export function OtherDates({
  siblings,
  detailsUrl,
  title,
}: {
  /** Already trimmed to what is still to come, and in order. */
  siblings: Sibling[];
  detailsUrl: string;
  /** The event's own name, for the sheet's heading. */
  title: string;
}) {
  const [open, setOpen] = useState(false);
  if (siblings.length === 0) return null;

  const hiding = siblings.length >= WORTH_HIDING;
  const shown = hiding ? siblings.slice(0, MAX_ON_PAGE) : siblings;

  return (
    <section>
      <h2 className="kicker mb-3">Other dates</h2>

      <ul className="card divide-mist divide-y">
        {shown.map((s) => (
          <DateRow key={s.id} s={s} detailsUrl={detailsUrl} />
        ))}
      </ul>

      {hiding && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-slate-deep hover:text-navy mt-3 flex items-center gap-2 text-sm font-semibold"
        >
          <CalendarDays className="text-teal h-4 w-4" />
          View all {siblings.length} dates
        </button>
      )}

      <ResponsiveSheet
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="max-w-lg"
        panelClassName="bg-ground text-ink md:rounded-3xl"
        noPanelPadding
        header={
          <div className="ed px-5 pt-5 pb-4">
            <div className="flex items-center gap-3">
              <span className="flex h-4 w-5 shrink-0 items-center justify-center">
                <CalendarDays className="text-teal h-5 w-5" />
              </span>
              <p className="kicker">Other dates</p>
            </div>
            <p className="text-navy mt-2 pl-8 text-lg font-extrabold">{title}</p>
            <p className="text-slate pl-8 text-sm">
              {siblings.length} more {siblings.length === 1 ? 'date' : 'dates'}
            </p>
          </div>
        }
      >
        <SheetPage name="main">
          <div className="ed px-5 pb-8">
            <ul className="card divide-mist divide-y">
              {siblings.map((s) => (
                <DateRow key={s.id} s={s} detailsUrl={detailsUrl} />
              ))}
            </ul>
          </div>
        </SheetPage>
      </ResponsiveSheet>
    </section>
  );
}
